/**
 * @file poQtyDisplay.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-21
 *
 * "Show quantities as" on a purchase order (Cam, 2026-10-08) — the client's
 * side of utils/poQtyDisplay.js on the server, which does the formatting: a
 * line counted in packs (a known unit weight that is not a tonne) prints on
 * two lines, tonnes first ("2.00 (T)" over "in 500kg totes (4)") or packs
 * first ("4 (500kg totes)" over "2.00 T") — the tonnes on the quantity rule
 * (utils/numberFormatters formatQuantity), the pack count a count (formatCount). Tonne lines and unknown units are unchanged.
 *
 * The server sends each line's two lines already worked out (qtyDisplay on
 * the line).
 *
 * 2.0.0 (2026-10-21, Cam — "The option to show packs or tonnes first is
 * confusing"): the choice is gone, and with it its wording and default rule
 * (QTY_DISPLAY_OPTIONS, QTY_DISPLAY_HINT, defaultQtyDisplay,
 * normaliseQtyDisplay). A line counted in packs shows its packs first, a
 * tonne line its tonnes — the unit entered on the line decides. What is left
 * is how a PO line's quantity is written (a count or the quantity rule).
 */

import { formatCount, formatCountInput, formatQuantity, formatDecimalInput } from './numberFormatters';
import { isNoWeight } from './lineUnits';

/**
 * Does this PO line's quantity COUNT things — packs or pallets — rather than
 * measure them? Then it shows only the places it has ("40", "3"), the
 * quantity rule's one exception (Cam, 2026-10-08). A Direct Delivery haulage
 * line counts pallets; a PO-H line counts packs (unless it says tonnes); any
 * other line counts when its unit suffix says "× 25kg bag" (or "pallets").
 * Tonnes and an unknown unit measure. The server's utils/poQtyDisplay
 * isCountedLine is the same rule.
 */
export function isCountedLine({ kind = null, unitSuffix = null, unitWeightKg = null } = {}) {
  if (kind === 'haulage') return true;
  // A line set to None — no weight (utils/lineUnits, Cam 2026-10-12): a
  // plain count ("1"), with no unit.
  if (kind !== 'po_h' && isNoWeight(unitWeightKg)) return true;
  const suffix = String(unitSuffix ?? '').trim();
  if (kind === 'po_h') return suffix !== 'T';
  return suffix.startsWith('×') || suffix === 'pallets';
}

/** A PO line's quantity as text: a count ("40") or the quantity rule ("12.50"). */
export function lineQtyText(quantity, line = {}) {
  return (isCountedLine(line) ? formatCount : formatQuantity)(quantity);
}

/** The same, for the VALUE of an input (no thousands separators). */
export function lineQtyInput(quantity, line = {}) {
  return (isCountedLine(line) ? formatCountInput : formatDecimalInput)(quantity);
}
